import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trophy, RefreshCw } from "lucide-react";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
} from "recharts";
import { toast } from "sonner";

// Single accent color — the chart now combines every patient across all
// pharmacies (per the operator's request "do not split by pharmacy") so
// there's no pharmacy palette to encode. Pharmacy nickname still appears
// as a smaller subtitle beneath the patient name on the Y axis + inside
// the tooltip.
const BAR_COLOR = "#0EA5A4"; // teal-500 (app primary)

const pad2 = (n) => String(n).padStart(2, "0");
const currentMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};
const lastMonthStr = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};
const humanMonth = (ym) => {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return ym || "";
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
};

// Two-line Y-axis tick: bold patient name on top, muted pharmacy nickname(s)
// below. Recharts hands us `payload.value` (the dataKey) and the row's
// full payload via the chart context, but here we look up the row via the
// `payload.index` since dataKey is just the display string.
function PatientTick({ x, y, payload, rows }) {
  const row = rows[payload.index];
  if (!row) return null;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={-8} y={-4} textAnchor="end" fill="hsl(var(--foreground))" fontSize={12} fontWeight={600}>
        {row.name}
      </text>
      <text x={-8} y={10} textAnchor="end" fill="hsl(var(--muted-foreground))" fontSize={10}>
        {row.pharmacy || "—"}
      </text>
    </g>
  );
}

export default function TopPatientsChart() {
  const [month, setMonth] = useState(currentMonthStr());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (m = month) => {
    setLoading(true);
    try {
      const r = await api.get("/patients/top-by-month", { params: { month: m, limit: 10 } });
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [month]);

  // Chart data — patients ranked desc so the biggest bar is at the top.
  const chartData = useMemo(() =>
    rows.map((r, idx) => {
      const display = (r.patient_name || "—").length > 26
        ? (r.patient_name || "").slice(0, 24) + "…"
        : (r.patient_name || "—");
      // Pharmacy label: prefer the joined nickname list (may contain 1..N).
      // Fall back to raw names if no nicknames are set.
      const pharmacy = (r.pharmacy_nickname && r.pharmacy_nickname.trim())
        || ((r.pharmacy_names || []).join(", "))
        || "—";
      return {
        rank: idx + 1,
        name: display,
        full_name: r.patient_name,
        phone: r.phone,
        pharmacy,
        count: r.order_count,
      };
    }),
  [rows]);

  const totalOrders = useMemo(() => rows.reduce((s, r) => s + (r.order_count || 0), 0), [rows]);

  return (
    <Card className="p-4 lg:p-5 mb-6" data-testid="top-patients-chart">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
            <Trophy className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="text-sm font-medium">Top 10 Patients · Orders by Month</div>
            <div className="text-xs text-muted-foreground">
              {humanMonth(month)} · {totalOrders} order{totalOrders === 1 ? "" : "s"} across the top 10
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant={month === currentMonthStr() ? "default" : "outline"}
            className="rounded-full h-8"
            onClick={() => setMonth(currentMonthStr())}
            data-testid="top-patients-current-month"
          >
            This month
          </Button>
          <Button
            size="sm"
            variant={month === lastMonthStr() ? "default" : "outline"}
            className="rounded-full h-8"
            onClick={() => setMonth(lastMonthStr())}
            data-testid="top-patients-last-month"
          >
            Last month
          </Button>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value || currentMonthStr())}
            className="h-8 w-[160px] rounded-full"
            data-testid="top-patients-month-input"
          />
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full h-8"
            onClick={() => load(month)}
            data-testid="top-patients-refresh"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
          Loading top patients…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="h-40 flex items-center justify-center text-sm text-muted-foreground"
          data-testid="top-patients-empty"
        >
          No orders in {humanMonth(month)}.
        </div>
      ) : (
        <div style={{ width: "100%", height: Math.max(300, rows.length * 44 + 40) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 4, right: 32, left: 8, bottom: 4 }}
              barCategoryGap={8}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
              <XAxis
                type="number"
                allowDecimals={false}
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={200}
                interval={0}
                tick={<PatientTick rows={chartData} />}
              />
              <Tooltip
                cursor={{ fill: "hsl(var(--muted))" }}
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid hsl(var(--border))",
                  fontSize: 12,
                }}
                formatter={(value, _key, ctx) => [
                  `${value} order${value === 1 ? "" : "s"}`,
                  ctx?.payload?.pharmacy || "",
                ]}
                labelFormatter={(_lbl, payload) => {
                  const p = payload?.[0]?.payload;
                  if (!p) return "";
                  return `#${p.rank} · ${p.full_name}${p.phone ? " · " + p.phone : ""}`;
                }}
              />
              <Bar dataKey="count" radius={[0, 6, 6, 0]} fill={BAR_COLOR}>
                <LabelList
                  dataKey="count"
                  position="right"
                  style={{ fill: "hsl(var(--foreground))", fontSize: 11, fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
